import { AfterViewInit, Component, DestroyRef, ElementRef, HostListener, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { ApsPermissionService } from '../../services/aps-permission/aps-permission.service';
import { ApsPermitedScreen } from '../../models/aps-permission/aps-permited-screen';
import { Router } from '@angular/router';

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss'],
    standalone: false
})
export class HomeComponent implements OnInit, AfterViewInit, OnDestroy {
	private readonly destroyRef = inject(DestroyRef);
	@ViewChild('homeTitle') homeTitle?: ElementRef<HTMLHeadingElement>;

	allPermissions: string[] = [];
	permissionsLoaded = false;
	isLoadingPermissions = false;
	isScreenSales = false;
	isScreenSalesTelemarketing = false;
	isScreenContractSearch = false;
	isScreenContractSearchDealer = false;
	isAccessManagement = false;
	homeTitleFontSizePx = 48;
	private readonly homeTitleMaxFontSizePx = 48;
	private readonly homeTitleMinFontSizePx = 16;
	private titleFitTimer: ReturnType<typeof setTimeout> | null = null;

	constructor(
		private apsPermissionService: ApsPermissionService,
		private router: Router,
		private translateService: TranslateService,
	) { }

	ngOnInit() {
		this.translateService.onLangChange
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((_event: LangChangeEvent) => {
				this.scheduleHomeTitleFit();
			});

		this.initializePermissions();
	}

	ngAfterViewInit(): void {
		this.scheduleHomeTitleFit();
	}

	ngOnDestroy(): void {
		document.body.classList.remove('home-no-scroll');

		if (this.titleFitTimer !== null) {
			clearTimeout(this.titleFitTimer);
			this.titleFitTimer = null;
		}
	}

	@HostListener('window:resize')
	onWindowResize(): void {
		this.scheduleHomeTitleFit();
	}

	checkPermission(permissionName: string): boolean {
		return this.allPermissions.includes(permissionName);
	}

	loadScreenPermissions() {
		this.isScreenSales = this.checkPermission(ApsPermitedScreen.screenSales);
		this.isScreenSalesTelemarketing = this.checkPermission(ApsPermitedScreen.screenSalesTelemarketing);
		this.isScreenContractSearch = this.checkPermission(ApsPermitedScreen.screenContractSearch);
		this.isScreenContractSearchDealer = this.checkPermission(ApsPermitedScreen.screenContractSearchDealer);
		this.isAccessManagement = this.checkPermission(ApsPermitedScreen.accessManagement);
	}

	async getListAllPermissions(): Promise<string[]> {
		const cachedPermission = this.apsPermissionService.getApsPermission();
		if (cachedPermission?.Permitted?.length) {
			const cachedList: string[] = [];
			cachedPermission.Permitted.forEach((permitted) => {
				permitted.execute.forEach((permissionName) => {
					cachedList.push(permissionName);
				});
			});
			return cachedList;
		}

		let allPermissions: string[] = [];
		await this.apsPermissionService.getListPermissions().then((list: string[]) => {
			allPermissions = list;
		});
		return allPermissions;
	}

	async initializePermissions(forceReload = false) {
		if (this.permissionsLoaded && !forceReload) {
			return;
		}

		if (this.isLoadingPermissions) {
			return;
		}

		this.isLoadingPermissions = true;
		this.allPermissions = await this.getListAllPermissions();
		this.loadScreenPermissions();
		this.permissionsLoaded = true;
		this.isLoadingPermissions = false;
		this.scheduleHomeTitleFit();
	}

	private scheduleHomeTitleFit(): void {
		if (this.titleFitTimer !== null) {
			clearTimeout(this.titleFitTimer);
		}

		this.titleFitTimer = setTimeout(() => {
			this.titleFitTimer = null;
			this.fitHomeTitleToSingleLine();
		}, 0);
	}

	private fitHomeTitleToSingleLine(): void {
		const titleElement = this.homeTitle?.nativeElement;
		const containerElement = titleElement?.parentElement;

		if (!titleElement || !containerElement) {
			return;
		}

		const availableWidth = containerElement.clientWidth;
		let nextFontSize = this.homeTitleMaxFontSizePx;

		titleElement.style.fontSize = `${nextFontSize}px`;

		while (titleElement.scrollWidth > availableWidth && nextFontSize > this.homeTitleMinFontSizePx) {
			nextFontSize -= 1;
			titleElement.style.fontSize = `${nextFontSize}px`;
		}

		this.homeTitleFontSizePx = nextFontSize;
	}

	async salesPermissions() {
		if (!this.permissionsLoaded) {
			await this.initializePermissions();
		}

		if (this.isScreenSales) {

			this.router.navigate(['/sales-channel']);
		}
		else if (this.isScreenSalesTelemarketing) {

			this.router.navigate(['/sales-channel-external']);
		}

	}

	async dealerPermissions() {
		if (!this.permissionsLoaded) {
			await this.initializePermissions();
		}

		if (this.isScreenContractSearchDealer) {

			this.router.navigate(['/contract-search-dealer']);
		}
		else if (this.isScreenContractSearch) {

			this.router.navigate(['/contract-search']);
		}

	}

}